import React, { useEffect, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { Progress, Table, TableCell } from "semantic-ui-react";
import { getClientDetailedSaleStats } from "../../api";
import { useReportDates } from "../../hooks/useReportDates";
import { SalesReportData } from "../../hooks/useSalesReportData";
import { ClientDetailedSaleStats } from "../../types";
import { getReportClientName } from "../../utils";
import { ExpandButton } from "../ExpandButton/ExpandButton";
import { FormattedAmount } from "../FormattedAmount";
import { RatioCell } from "../RatioCell";
import { DetailedStats } from "./DetailedStats";
import { MonthTotalCell } from "./MonthTotalCell";
import { PeriodTotalCell } from "./PeriodTotalCell";

const m = defineMessages({
    previous: { id: "OrdersReportTable.ClientRows.Previous", defaultMessage: "Previous" },
    current: { id: "OrdersReportTable.ClientRows.Current", defaultMessage: "Current" },
    onOrder: { id: "OrdersReportTable.ClientRows.OnOrder", defaultMessage: "On order: " },
    billed: { id: "OrdersReportTable.ClientRows.Billed", defaultMessage: "Billed: " },
});

interface TableClientRowsProps {
    clientCode: string;
    salesReportData: SalesReportData;
}

export const TableClientRows: React.FC<TableClientRowsProps> = ({
    clientCode,
    salesReportData,
}) => {
    const { formatMessage } = useIntl();

    const {
        getCurrentPeriodYear,
        getPreviousPeriodYear,
        currentPeriodStartDate,
        currentPeriodEndDate,
        previousPeriodStartDate,
        previousPeriodEndDate,
    } = useReportDates();

    const [isExpanded, setIsExpanded] = useState(false);
    const [detailedStats, setDetailedStats] = useState<ClientDetailedSaleStats>();
    const [isLoadingDetailedStats, setIsLoadingDetailedStats] = useState(false);

    useEffect(() => {
        if (!isExpanded || detailedStats) return;

        setIsLoadingDetailedStats(true);

        getClientDetailedSaleStats(clientCode, previousPeriodStartDate, currentPeriodEndDate).then(
            (stats) => {
                setDetailedStats(stats);
                setIsLoadingDetailedStats(false);
            },
        );
    }, [clientCode, currentPeriodEndDate, detailedStats, isExpanded, previousPeriodStartDate]);

    const clientName = getReportClientName(
        salesReportData.clientInformationByClientCode,
        clientCode,
    );
    const monthlySales = salesReportData.monthlySalesByClientCode[clientCode];

    const incomingBillingAmount =
        salesReportData.incomingBillingAmountByClientCode[clientCode] || 0;

    const billedAmountAfterEndDate =
        salesReportData.billedAmountAfterEndDateByClientCode[clientCode] || 0;

    const currentPeriodRow = (
        <Table.Row>
            <TableCell rowSpan={2} style={{ verticalAlign: "baseline" }}>
                <div>{clientName}</div>
                <div style={{ color: "#686869" }}>{clientCode}</div>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{formatMessage(m.onOrder)}</div>
                    <div>
                        <FormattedAmount amount={incomingBillingAmount} />
                    </div>
                </div>
            </TableCell>
            <TableCell>{formatMessage(m.current)}</TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <MonthTotalCell
                    key={month}
                    clientCode={clientCode}
                    total={monthlySales?.statsByMonth[month]?.currentPeriodTotal}
                    month={month}
                    year={getCurrentPeriodYear(month)}
                />
            ))}
            <PeriodTotalCell
                clientCode={clientCode}
                total={monthlySales?.currentPeriodTotal}
                startDate={currentPeriodStartDate}
                endDate={currentPeriodEndDate}
            />
        </Table.Row>
    );

    const previousPeriodRow = (
        <Table.Row style={{ borderTop: "none" }}>
            <TableCell style={{ borderTop: "none", whiteSpace: "nowrap" }}>
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <div>{formatMessage(m.billed)}</div>
                    <div>
                        <FormattedAmount amount={billedAmountAfterEndDate} />
                    </div>
                </div>
            </TableCell>
            <TableCell style={{ borderTop: "none" }}>{formatMessage(m.previous)}</TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <MonthTotalCell
                    key={month}
                    clientCode={clientCode}
                    total={monthlySales?.statsByMonth[month]?.previousPeriodTotal}
                    month={month}
                    year={getPreviousPeriodYear(month)}
                    style={{ borderTop: "none" }}
                />
            ))}
            <PeriodTotalCell
                clientCode={clientCode}
                total={monthlySales?.previousPeriodTotal}
                startDate={previousPeriodStartDate}
                endDate={previousPeriodEndDate}
                style={{ borderTop: "none" }}
            />
        </Table.Row>
    );

    const ratiosRow = (
        <Table.Row style={{ borderTop: "none" }}>
            <TableCell style={{ borderTop: "none" }}>
                <ExpandButton isExpanded={isExpanded} onChange={setIsExpanded} />
            </TableCell>
            <TableCell style={{ borderTop: "none" }}></TableCell>
            <TableCell style={{ borderTop: "none" }}></TableCell>
            {salesReportData.monthsInOrder.map((month) => (
                <RatioCell
                    key={month}
                    style={{ borderTop: "none" }}
                    ratio={monthlySales?.statsByMonth[month]?.ratio}
                    previousAmount={monthlySales?.statsByMonth[month]?.previousPeriodTotal}
                    currentAmount={monthlySales?.statsByMonth[month]?.currentPeriodTotal}
                />
            ))}
            <RatioCell
                style={{ borderTop: "none" }}
                ratio={monthlySales?.ratio}
                previousAmount={monthlySales?.previousPeriodTotal}
                currentAmount={monthlySales?.currentPeriodTotal}
            />
        </Table.Row>
    );

    const detailedStatsLoader = isLoadingDetailedStats && (
        <Table.Row style={{ backgroundColor: "#F9FAFB" }}>
            <TableCell colSpan={16}>
                <Progress percent={100} color="blue" indicating inverted size="tiny" />
            </TableCell>
        </Table.Row>
    );

    return (
        <>
            {currentPeriodRow}
            {previousPeriodRow}
            {ratiosRow}
            {isExpanded && (
                <>
                    {detailedStatsLoader}
                    <DetailedStats
                        detailedStats={detailedStats}
                        reportMonthsInOrder={salesReportData.monthsInOrder}
                        outputCurrency={salesReportData.outputCurrency}
                    />
                </>
            )}
        </>
    );
};
